/**
 * **色板** —— acid design：黑底、白半调、荧光酸色。
 *
 * 评审要求「先定义色板再做效果」，所以这里是**唯一**的颜色来源。
 *
 * ## 这一版的取向（和第一版相反，第一版做反了）
 *
 * | | 第一版（错） | 现在 |
 * | --- | --- | --- |
 * | 底 | 奶黄纸（亮） | **纯黑 `#000000`** |
 * | 半调 | 深色点印在亮纸上 | **白点印在黑底上** |
 * | 酸色饱和度 | 刻意压低到 0.6 以下 | **拉上来（0.6 以上）** |
 *
 * 「降饱和」的目的**只是长时间游玩不刺眼**，不是把颜色调淡。所以：
 *
 * - **底色用纯黑**，不靠降低颜色饱和度来"柔和"
 * - 酸色是**真的酸** —— 高饱和荧光色
 * - 需要收敛的是**大面积/长时间盯着看**的东西（面板、线条），
 *   不是荧光色本身。见下面 `STROKE` 与 `SIGNAL` 的分工
 *
 * ## 三层分工
 *
 * | 层 | 是什么 | 用在哪 |
 * | --- | --- | --- |
 * | `SIGNAL` | 荧光基色**原样** | 发光、能量脉冲、选中光晕、高亮 |
 * | `STROKE` | 同色相，**往中灰收一点** | 细线、描边、纸上的小字 |
 * | `BASE` / `LIGHT` | 黑底 / 白字白点 | 大面积底、文字、半调 |
 *
 * ⚠️ 为什么 `STROKE` 要收一点：荧光色画成**细线**没问题，
 * 但大面积铺开、或者长时间盯着高饱和的边线看会累。
 * 收的是**饱和度**不是明度 —— 黑底上明度一低就看不见了。
 */

// ============================================================
// 荧光基色
// ============================================================

/**
 * 酸色系。
 *
 * 饱和度都在 **0.6 以上** —— 这是"acid"的下限，
 * `test/render/palette.test.ts` 里有一条断言在盯着。
 *
 * ⚠️ 这些是**原料**，不是语义。业务代码从 `SIGNAL` / `STROKE` 取。
 */
export const ACID = {
  /** 酸绿 —— 主色 */
  lime: 0xc1ef39,
  /** 酸柠黄 */
  citron: 0xe8f23c,
  /** 酸薄荷（青绿） */
  mint: 0x3ce8a0,
  /** 酸青 */
  cyan: 0x3ce0e0,
  /** 酸天蓝 */
  sky: 0x3caaf0,
  /** 酸紫 */
  violet: 0xa03cf0,
  /** 酸品红 */
  magenta: 0xf03cd0,
  /** 酸玫红 —— 偏冷的红，当"危险"用不会像警告牌 */
  rose: 0xf43c62,
  /** 酸珊瑚（橙红） */
  coral: 0xf4733c,
  /** 酸琥珀（橙黄） */
  amber: 0xf0b53c,
} as const;

// ============================================================
// 底与前景
// ============================================================

/**
 * 底。
 *
 * ⚠️ `void` 是**纯黑**，不是"很深的灰"。半调的白点印在这上面，
 * 底一旦不是纯黑，白点的对比度就上不去，整块背景会发灰。
 */
export const BASE = {
  /** 大面积底色。**纯黑** */
  void: 0x000000,
  /** 抬起来一档的面板（深灰带一点绿，和酸色同色系） */
  raised: 0x11140d,
  /** 再抬一档。分隔线、凹陷 */
  sunken: 0x1e2317,
  /** 遮罩层底色（配 alpha 用） */
  scrim: 0x000000,
} as const;

/**
 * 黑底上的前景色。
 *
 * `halftone` 就是半调网点的**墨色** —— 评审要的"白半调"。
 */
export const LIGHT = {
  /** 正文。纯白 */
  base: 0xffffff,
  /** 次要文字 */
  dim: 0x9aa189,
  /** 半调网点的白。**这就是"白半调"的那个白** */
  halftone: 0xffffff,
} as const;

// ============================================================
// 语义色
// ============================================================

/**
 * 按**用途**取色。业务代码用这一层。
 *
 * 这一层是**荧光基色原样**，用在会发光的东西上：
 * 能量脉冲、选中光晕、高亮、需要"跳出来"的地方。
 */
export const SIGNAL = {
  /** 主强调：当前选中、主按钮 */
  accent: ACID.lime,
  /** 危险 / 濒死 / 致命牌。**濒死横带的红辉光和异常牌的威慑都用它** */
  danger: ACID.rose,
  /** 警告 / 攻击 */
  warning: ACID.amber,
  /** 正常 / 成功 / 救命牌 */
  ok: ACID.mint,
  /** 信息 / 打断牌 */
  info: ACID.cyan,
  /** 特殊 / 连击 */
  special: ACID.violet,
  /** 中立强调 */
  neutral: ACID.sky,
} as const;

/**
 * **线条色** —— 同色相，往中灰收一点。
 *
 * ⚠️ 收的是**饱和度**，不是明度。黑底上明度一低就看不见了，
 * 所以判据是「和纯黑的对比度仍 ≥ 3:1」（WCAG 1.4.11 对非文字图形的下限）。
 *
 * 为什么要这一档：荧光色**细线**没问题，但长时间盯着高饱和的边线看会累。
 * 这是"降饱和是为了不刺眼"这条要求真正落地的地方 ——
 * 落在**线**上，而不是落在底色上。
 */
export const STROKE = {
  accent: desaturate(ACID.lime, 0.24),
  danger: desaturate(ACID.rose, 0.24),
  warning: desaturate(ACID.amber, 0.24),
  ok: desaturate(ACID.mint, 0.24),
  info: desaturate(ACID.cyan, 0.24),
  special: desaturate(ACID.violet, 0.24),
  neutral: desaturate(ACID.sky, 0.24),
} as const;

/**
 * **印刷专色** —— 唯一一个**不在酸色板里**的墨。
 *
 * ⚠️ 它只属于一张牌：`Exception` 白卡上的红楔子（Lissitzky 1919）。
 * 它刻意**不是**酸色 —— 酸色是"这台机器印的"，而这管红墨是那台机器**换上去的**。
 *
 * 为什么不能用 `ACID.rose` / `ACID.coral` 代替：
 * 那两个是荧光色，在黑底上是"发光"；这个红要**印在白纸上**，
 * 必须是实心颜料的感觉。混用会让白卡失去"它不属于这个媒介"这件事。
 *
 * ## ⚠️ 使用纪律：**「标记」可以到处是，「动作」只属于异常**
 *
 * 这管墨原来写着"只有 `Exception` 能用"。红楔子后来成了**游戏自己的标记**
 * （Logo / favicon），那条唯一性没法再照字面守 —— 所以改成按**用法**分：
 *
 * | | 是什么 | 允许在哪 |
 * | --- | --- | --- |
 * | **标记**（stamp） | 小、被框住、**不出血** | Logo / favicon / 品牌位置 |
 * | **动作**（the act） | 满版、**出血**、刺穿环 | **只属于 `Exception`** |
 *
 * 于是红楔子是全套的图腾，而异常仍然是唯一一张**楔子变成了武器**的牌。
 * 完整的分工见 `docs/card-faces.md` §5.1。
 *
 * ⚠️ 仍然不许的是：**任何一张别的牌**把楔子当内容画进去。
 * 那是"动作"，只有异常有资格。
 */
export const PRESS = {
  /** 印刷红。Lissitzky《用红楔子打败白人》的那个红 */
  red: 0xe0261f,
} as const;

// ============================================================
// 换算与派生
// ============================================================

/**
 * `0xc1ef39` → `[0.76, 0.94, 0.22]`（0..1 浮点）。
 *
 * 着色器 uniform 要的是归一化浮点，别在每个调用点上手写 `/ 255` ——
 * 写漏一次就是一个颜色偏掉几百倍、但完全看不出哪里错的 bug。
 */
export function rgb01(hex: number): readonly [number, number, number] {
  return [((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255];
}

/** 同上，但带 alpha */
export function rgba01(hex: number, alpha = 1): readonly [number, number, number, number] {
  const [r, g, b] = rgb01(hex);
  return [r, g, b, alpha];
}

/** `0xc1ef39` → `'#c1ef39'`。给 DOM / 调试输出用 */
export function hexString(hex: number): string {
  return `#${hex.toString(16).padStart(6, "0")}`;
}

/** 两个颜色线性插值。补间动画和渐变用它，避免每处各写一遍 */
export function mixHex(from: number, to: number, t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const ch = (shift: number): number => {
    const a = (from >> shift) & 0xff;
    const b = (to >> shift) & 0xff;
    return Math.round(a + (b - a) * k);
  };
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

/** 按比例压暗（0 = 原色，1 = 全黑） */
export function darken(hex: number, amount: number): number {
  return mixHex(hex, 0x000000, amount);
}

/** 按比例提亮（0 = 原色，1 = 全白） */
export function lighten(hex: number, amount: number): number {
  return mixHex(hex, 0xffffff, amount);
}

/**
 * 往**等明度的灰**收 —— 降饱和但基本不动明度。
 *
 * ⚠️ 这和 `darken()` 是两件事。黑底上的线条不能用 `darken()` 收敛：
 * 一暗就看不见了。要收的是"花不花"，不是"亮不亮"。
 */
export function desaturate(hex: number, amount: number): number {
  const [r, g, b] = rgb01(hex);
  const luma = 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
  const gray = Math.min(1, Math.max(0, luma));
  const k = amount < 0 ? 0 : amount > 1 ? 1 : amount;
  const mix = (c: number): number => Math.round((c + (gray - c) * k) * 255);
  return (mix(r ?? 0) << 16) | (mix(g ?? 0) << 8) | mix(b ?? 0);
}

/** 整块色板 —— 调试面板和文档用它遍历 */
export const PALETTE = { ACID, BASE, LIGHT, SIGNAL, STROKE, PRESS } as const;
